import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { useAgency } from '@/hooks/useAgency'
import { useTierFeatures } from '@/hooks/useTierFeatures'
import { useOnboarding } from '@/hooks/useOnboarding'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/lib/supabase'
import { Project } from '@/types'
import { Plus, Command, Upload, X, ChevronDown } from 'lucide-react'
import { cn } from '@/utils/cn'
import {
  canCreateProject,
  getProjectsRemaining,
  isAdmin,
  isManager,
  isClient,
  isSuperAdmin,
  hasAdminPrivileges,
} from '@/utils/permissions'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { DashboardSkeleton } from '@/components/DashboardSkeleton'
import { AgencyManagementModal } from '@/components/AgencyManagementModal'
import { DashboardProjects } from '@/components/dashboard/DashboardProjects'
import { PortfolioDashboard } from '@/components/dashboard/PortfolioDashboard'
import { DashboardTokenUsage } from '@/components/DashboardTokenUsage'
import { DashboardCommandPalette } from '@/components/dashboard/DashboardCommandPalette'
import { NewProjectModal } from '@/components/dashboard/NewProjectModal'
import { WelcomeOnboarding } from '@/components/onboarding/WelcomeOnboarding'
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist'
import { BulkProjectImport } from '@/components/admin/BulkProjectImport'

type ViewMode = 'grid' | 'list'

export const Dashboard = () => {
  const { t } = useTranslation(['dashboard', 'common'])
  const { user } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()

  // Projects state
  const [projects, setProjects] = useState<Project[]>([])
  const [projectCount, setProjectCount] = useState(0)
  const [loading, setLoading] = useState(true)

  // Modal state
  const [showNewProjectModal, setShowNewProjectModal] = useState(false)
  const [showAgencyManagementModal, setShowAgencyManagementModal] = useState(false)
  const [showCommandPalette, setShowCommandPalette] = useState(false)
  const [showBulkImport, setShowBulkImport] = useState(false)

  // New project form state
  const [newProjectName, setNewProjectName] = useState('')
  const [selectedAgencyId, setSelectedAgencyId] = useState('')
  const [agencies, setAgencies] = useState<any[]>([])

  // Search/filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'updated' | 'name' | 'created'>('updated')
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem('dashboard-view-mode') as ViewMode) || 'grid'
  })

  // Admin section collapse state
  const [adminSectionOpen, setAdminSectionOpen] = useState(false)

  const { agency } = useAgency(user?.agency_id)
  const { tier } = useTierFeatures()
  const userCanCreateProject = canCreateProject(user, agency, projectCount)
  const userHasAdminPrivileges = hasAdminPrivileges(user)
  const projectsRemaining = getProjectsRemaining(agency, projectCount)
  const isAtProjectLimit = tier === 'free' && projectsRemaining === 0

  const {
    showOnboarding, showChecklist, onboardingProjectId,
    completeStep, skipOnboarding, completeOnboarding, dismissChecklist,
  } = useOnboarding(projects, loading)

  // Persist view mode
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode)
    localStorage.setItem('dashboard-view-mode', mode)
  }

  // Cmd+K listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setShowCommandPalette(prev => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    loadProjects()
    if (isSuperAdmin(user)) {
      loadAgencies()
    }
  }, [user])

  const loadAgencies = async () => {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true })
      if (error) throw error
      setAgencies(data || [])
    } catch (error) {
      console.error('Error loading agencies:', error)
    }
  }

  const loadProjects = async () => {
    if (!user) return
    try {
      let query = supabase
        .from('projects')
        .select('*, agencies(name)')
        .order('updated_at', { ascending: false })

      if (isSuperAdmin(user)) {
        // Super admin sees all
      } else if (isAdmin(user)) {
        const { data: memberData } = await supabase
          .from('project_members')
          .select('project_id')
          .eq('user_id', user.id)
        const memberProjectIds = memberData?.map(m => m.project_id) || []
        if (memberProjectIds.length > 0) {
          query = query.or(`agency_id.eq.${user.agency_id},id.in.(${memberProjectIds.join(',')})`)
        } else {
          query = query.eq('agency_id', user.agency_id)
        }
      } else if (isManager(user) || isClient(user)) {
        const { data: memberData } = await supabase
          .from('project_members')
          .select('project_id')
          .eq('user_id', user.id)
        const projectIds = memberData?.map((m) => m.project_id) || []
        if (projectIds.length > 0) {
          query = query.in('id', projectIds)
        } else {
          setProjects([])
          setProjectCount(0)
          setLoading(false)
          return
        }
      }

      const { data, error } = await query
      if (error) throw error
      setProjects(data || [])
      setProjectCount(data?.length || 0)
    } catch (error: any) {
      console.error('Error loading projects:', error)
    } finally {
      setLoading(false)
    }
  }

  const createProject = async () => {
    await createStandardProject()
  }

  const createStandardProject = async () => {
    if (!newProjectName.trim() || !userCanCreateProject) return
    const agencyId = isSuperAdmin(user) ? selectedAgencyId : user!.agency_id
    if (!agencyId) {
      toast({ variant: 'destructive', title: t('common:errors.validationError'), description: t('common:actions.selectAgency') })
      return
    }
    try {
      const { data, error } = await supabase
        .from('projects')
        .insert({
          name: newProjectName, user_id: user!.id, agency_id: agencyId, created_by: user!.id,
          status: 'draft', project_type: 'standard', source_url: null, current_step: 1,
          data: { phase1: {}, phase2: {}, phase3: {} },
        })
        .select().single()
      if (error) throw error
      if (!isSuperAdmin(user)) {
        const memberRole: 'manager' | 'client' = (isAdmin(user) || isManager(user)) ? 'manager' : 'client'
        await supabase.from('project_members').insert({ project_id: data.id, user_id: user!.id, role: memberRole })
      }
      resetModalState()
      await loadProjects()
      navigate(`/project/${data.id}`)
    } catch (error) {
      console.error('Error creating project:', error)
      toast({ variant: 'destructive', title: 'Error', description: t('dashboard:newProjectModal.errorCreate') })
    }
  }

  const handleSelectProject = (projectId: string) => {
    navigate(`/project/${projectId}`)
  }

  const resetModalState = () => {
    setShowNewProjectModal(false)
    setNewProjectName('')
    setSelectedAgencyId('')
  }

  if (loading) {
    return <DashboardSkeleton />
  }

  if (showOnboarding) {
    const resumeProject = onboardingProjectId
      ? projects.find(p => p.id === onboardingProjectId)
      : undefined
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <WelcomeOnboarding
          onComplete={() => loadProjects()}
          onSkip={skipOnboarding}
          completeStep={completeStep}
          completeOnboarding={completeOnboarding}
          initialStep={resumeProject ? 3 : undefined}
          resumeProject={resumeProject}
        />
      </div>
    )
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Onboarding Checklist (for users who skipped) */}
        {showChecklist && (
          <OnboardingChecklist
            onResume={() => window.location.reload()}
            onDismiss={dismissChecklist}
          />
        )}

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <p className="text-[11px] font-medium tracking-wide uppercase text-framed-neutral-500 mb-1">
              {t('dashboard:welcomeBack', { name: user?.name?.split(' ')[0] })}
            </p>
            <h1 className="text-2xl sm:text-3xl font-bold text-framed-neutral-800">{t('dashboard:title')}</h1>
          </div>
          <div className="flex items-center gap-3">
            {/* Bulk Import (super_admin only) */}
            {isSuperAdmin(user) && (
              <button
                onClick={() => setShowBulkImport(true)}
                className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-sm text-framed-neutral-500 bg-framed-neutral-100 rounded-2xl hover:bg-framed-neutral-200 hover:text-framed-neutral-700 transition-colors"
              >
                <Upload className="w-3.5 h-3.5" />
                Import
              </button>
            )}
            {/* Cmd+K hint */}
            <button
              onClick={() => setShowCommandPalette(true)}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-sm text-framed-neutral-400 bg-framed-neutral-100 rounded-2xl hover:bg-framed-neutral-200 hover:text-framed-neutral-500 transition-colors"
            >
              <Command className="w-3.5 h-3.5" />
              <span className="text-xs">K</span>
            </button>
            {userCanCreateProject ? (
              <button
                onClick={() => setShowNewProjectModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-framed-neutral-800 text-white rounded-xl font-medium hover:bg-framed-neutral-900 transition-colors"
              >
                <Plus className="w-5 h-5" />
                {t('dashboard:buttons.newProject')}
              </button>
            ) : isAtProjectLimit ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      disabled
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-framed-neutral-200 text-framed-neutral-500 rounded-xl font-medium cursor-not-allowed"
                    >
                      <Plus className="w-5 h-5" />
                      {t('dashboard:buttons.newProject')}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p>{t('dashboard:limits.projectLimitTooltip')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
          </div>
        </div>

        {/* Projects — main content */}
        <DashboardProjects
          projects={projects}
          user={user}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          sortBy={sortBy}
          onSortByChange={setSortBy}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          onNewProject={() => setShowNewProjectModal(true)}
          userCanCreateProject={userCanCreateProject}
        />

        {/* Admin Section — collapsible, admin-only */}
        {userHasAdminPrivileges && (
          <div className="mt-10">
            <button
              onClick={() => setAdminSectionOpen(!adminSectionOpen)}
              className="flex items-center gap-2 text-sm font-medium text-framed-neutral-500 hover:text-framed-neutral-700 transition-colors mb-4"
            >
              <ChevronDown
                className={cn(
                  'w-4 h-4 transition-transform duration-200',
                  adminSectionOpen && 'rotate-180'
                )}
              />
              Admin
            </button>
            {adminSectionOpen && (
              <div className="space-y-6">
                <DashboardTokenUsage />
                <PortfolioDashboard />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <NewProjectModal
        isOpen={showNewProjectModal}
        onClose={resetModalState}
        onCreateProject={createProject}
        user={user}
        newProjectName={newProjectName}
        onProjectNameChange={setNewProjectName}
        selectedAgencyId={selectedAgencyId}
        onAgencyIdChange={setSelectedAgencyId}
        agencies={agencies}
      />

      <AgencyManagementModal
        isOpen={showAgencyManagementModal}
        onClose={() => {
          setShowAgencyManagementModal(false)
          loadProjects()
        }}
      />

      {/* Command Palette */}
      {showCommandPalette && (
        <DashboardCommandPalette
          projects={projects}
          user={user}
          onClose={() => setShowCommandPalette(false)}
          onSelectProject={handleSelectProject}
          onNewProject={() => {
            setShowCommandPalette(false)
            setShowNewProjectModal(true)
          }}
          onNavigate={(path) => navigate(path)}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-framed-neutral-300">
              <h2 className="text-lg font-semibold text-framed-neutral-800">Bulk Project Import</h2>
              <button
                onClick={() => setShowBulkImport(false)}
                className="p-1.5 text-framed-neutral-500 hover:text-framed-neutral-800 hover:bg-framed-neutral-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {isSuperAdmin(user) && agencies.length > 0 && (
                <div className="mb-4">
                  <label className="block text-xs font-medium text-framed-neutral-600 mb-1">Agency</label>
                  <select
                    value={selectedAgencyId}
                    onChange={(e) => setSelectedAgencyId(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-framed-neutral-300 focus:outline-none focus:ring-2 focus:ring-framed-yellow-500/50"
                  >
                    <option value="">Select agency...</option>
                    {agencies.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {(selectedAgencyId || user?.agency_id) && (
                <BulkProjectImport
                  agencyId={selectedAgencyId || user!.agency_id}
                  onComplete={() => loadProjects()}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
